import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, Subject, of, timer } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';

import { StatusPageApi, StatusType } from '@vi-iot/client';

import { environment } from '../../../environments/environment';
import { AuthActions } from '../auth/auth.actions';

import { StatusPageActions } from './status-page.actions';
import { StatusPageStore } from './status-page.store';
import { Router } from '@angular/router';

@Injectable()
export class StatusPageEffects {
  getStatus$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(StatusPageActions.getStatus),
      concatLatestFrom(() => this.store.state$),
      switchMap(() =>
        timer(0, 5 * 60 * 1000).pipe(
          takeUntil(this.actions$.pipe(ofType(AuthActions.logout))),
          switchMap(() =>
            this.api.getAppStatus(environment.statusPageAppId).pipe(
              map((response) => {
                this.redirectOnStatusError(response.status);

                return StatusPageActions.getStatusSuccess({ status: response.status });
              }),
              catchError((error) => of(StatusPageActions.getStatusError({ error })))
            )
          )
        )
      )
    );
  });

  getStatusWithoutTimer$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(StatusPageActions.getStatusWithoutTimer),
      switchMap(() =>
        this.api.getAppStatus(environment.statusPageAppId).pipe(
          map((response) => {
            this.redirectOnStatusError(response.status);

            return StatusPageActions.getStatusSuccess({ status: response.status });
          }),
          catchError((error) => of(StatusPageActions.getStatusError({ error })))
        )
      )
    );
  });

  getMaintenances$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(StatusPageActions.getMaintenances),
      switchMap(() =>
        this.api.getMaintenances().pipe(
          map((response) =>
            response.filter(
              (maintenance) =>
                maintenance.components.some(
                  (component) => component.id === environment.statusPageAppId
                ) && new Date(maintenance.scheduled_for).getDate() - new Date().getDate() <= 8
            )
          ),
          map((maintenances) =>
            StatusPageActions.getMaintenancesSuccess({
              maintenances,
            })
          ),
          catchError(() => of(StatusPageActions.getMaintenancesError()))
        )
      )
    );
  });

  private redirectOnStatusError(status: StatusType): void {
    const unsubscribe: Subject<void> = new Subject<void>();
    const isUserOnStatusErrorPage = this.router.url.startsWith('status');
    const randomTimeBeforeNavigate = Math.floor(Math.random() * 5 * 60 * 1000);
    const isStatusError = status === StatusType.Major_Outage;

    if (!isUserOnStatusErrorPage && isStatusError) {
      this.router.navigate(['/status']);
    }

    if (isUserOnStatusErrorPage && status === StatusType.Operational) {
      timer(randomTimeBeforeNavigate)
        .pipe(takeUntil(unsubscribe))
        .subscribe(() => {
          this.router.navigate(['/home']);
          unsubscribe.next();
          unsubscribe.complete();
        });
    }
  }

  constructor(
    private api: StatusPageApi,
    private actions$: Actions,
    private store: StatusPageStore,
    private router: Router
  ) {}
}
